import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { eventBus, EventTypes, PageAction } from '@core/pubsub/EventBus';

interface ActionEntry {
    action: (() => Promise<void>) | null;
    disabled: boolean;
    isRunning: boolean;
    showOverlay: boolean;
}

// What each consumer (e.g. NewPageTitleBar) gets back per action key
export interface ActionState {
    hasAction: boolean;
    isRunning: boolean;
    isDisabled: boolean;
    trigger: () => Promise<void>;
}

interface PageActionContextType {
    registerAction: (
        key: PageAction,
        action: (() => Promise<void>) | null,
        disabled: boolean,
        showOverlay?: boolean // ← add optional
    ) => void;
    setActionDisabled: (key: PageAction, disabled: boolean) => void;
    getAction: (key: PageAction) => ActionState;
}

// Context
const PageActionContext = createContext<PageActionContextType | undefined>(undefined);

// Provider
export const PageActionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Registry: one entry per PageAction key that a page has registered
    const [registry, setRegistry] = useState<Partial<Record<PageAction, ActionEntry>>>({});

    // Registration
    const registerAction = useCallback(
        (key: PageAction, action: (() => Promise<void>) | null, disabled: boolean, showOverlay: boolean = true) => {
            setRegistry((prev) => {
                if (action === null) {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                }
                return {
                    ...prev,
                    [key]: { action, disabled, isRunning: false, showOverlay }
                };
            });
        },
        []
    );

    const setActionDisabled = useCallback((key: PageAction, disabled: boolean) => {
        setRegistry((prev) => {
            if (!prev[key]) return prev;
            return { ...prev, [key]: { ...prev[key]!, disabled } };
        });
    }, []);

    // Trigger
    const triggerAction = useCallback(
        async (key: PageAction) => {
            const entry = registry[key];
            if (!entry || !entry.action || entry.isRunning || entry.disabled) return;

            setRegistry((prev) => ({
                ...prev,
                [key]: { ...prev[key]!, isRunning: true }
            }));

            // Only notify app shell if this action wants the overlay
            if (entry.showOverlay) {
                eventBus.emit(EventTypes.PAGE_LOADING, { action: key });
            }

            try {
                await entry.action();
                if (entry.showOverlay) {
                    eventBus.emit(EventTypes.PAGE_LOADED, { action: key });
                }
            } catch (error) {
                console.error(`[PageActionContext] Action "${key}" failed:`, error);
                if (entry.showOverlay) {
                    eventBus.emit(EventTypes.PAGE_ERROR, { action: key, error });
                }
            } finally {
                setRegistry((prev) => ({
                    ...prev,
                    [key]: { ...prev[key]!, isRunning: false }
                }));
            }
        },
        [registry]
    );

    // Consumption helper
    const getAction = useCallback(
        (key: PageAction): ActionState => {
            const entry = registry[key];
            return {
                hasAction: !!entry?.action,
                isRunning: entry?.isRunning ?? false,
                isDisabled: entry?.disabled ?? false,
                trigger: () => triggerAction(key)
            };
        },
        [registry, triggerAction]
    );

    // Context value
    const value = useMemo<PageActionContextType>(
        () => ({
            registerAction,
            setActionDisabled,
            getAction
        }),
        [registry, registerAction, setActionDisabled, getAction]
    );

    return <PageActionContext.Provider value={value}>{children}</PageActionContext.Provider>;
};

// Hook
export const usePageActionContext = (): PageActionContextType => {
    const context = useContext(PageActionContext);
    if (!context) {
        throw new Error('usePageActionContext must be used within a PageActionProvider');
    }
    return context;
};
